import { Domain } from '@betterfunnel/types';
import * as Sentry from '@sentry/react';
import {
  DocumentData,
  QueryDocumentSnapshot,
  collection,
  onSnapshot,
} from 'firebase/firestore';
import { useCallback, useContext, useEffect, useState } from 'react';
import Loader from '../../components/Common/Loader';
import DomainCard from '../../components/Domains/DomainCard';
import DomainCardPlaceholder from '../../components/Domains/DomainCardPlaceholder';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import { useHasFeature } from '../../hooks/useHasFeature';
import { addDomain } from '../../services/DomainsService';
import { emptyConverter } from '../../utils';
import { track } from '../../utils/analytics';

export default function DomainsSettings() {
  const { selectedAccount } = useContext(UserContext);
  const { show } = useContext(SnackbarContext);

  const hasAddDomainFeature = useHasFeature('domains:add');

  const [domain, setDomain] = useState('');
  const [domainsList, setDomainsList] =
    useState<QueryDocumentSnapshot<Domain, DocumentData>[]>();
  const [disabled, setDisabled] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<{ domain: string; error: any } | null>(
    null,
  );

  useEffect(() => {
    if (domain.length == 0) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [domain]);

  useEffect(() => {
    if (adding) setDisabled(true);
  }, [adding]);

  useEffect(() => {
    if (!selectedAccount) {
      setDomainsList([] as any);
      return;
    }

    const domainsRef = collection(selectedAccount.ref, 'domains').withConverter(
      emptyConverter<Domain>(),
    );

    const unsubscribe = onSnapshot(domainsRef, (domains) => {
      setDomainsList(
        domains.docs.sort(
          (a, b) =>
            (b.data().createdAt?.seconds ?? 0) -
            (a.data().createdAt?.seconds ?? 0),
        ),
      );
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  const onAddDomainClick = useCallback(async () => {
    track('add_domain_button_click', {
      domain,
    });

    setAdding(true);
    try {
      await addDomain(selectedAccount?.id ?? '', domain);
      setDomain('');
    } catch (e: any) {
      Sentry.captureException(e);
      show(e.message);
      setError({ domain, error: e });
    } finally {
      setAdding(false);
    }
  }, [domain, selectedAccount?.id, show]);

  return (
    <div className="flex flex-col items-center justify-center py-2">
      {hasAddDomainFeature && (
        <>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              onAddDomainClick();
            }}
            className="mt-10 flex h-10 w-full max-w-2xl justify-between space-x-4 px-5"
          >
            <input
              type="text"
              name="domain"
              onInput={(e: any) => {
                setDomain(e.target.value);
              }}
              autoComplete="off"
              placeholder="mydomain.com"
              pattern="^(?:[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.)?[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$"
              required
              className="min-w-0 flex-auto rounded-md border border-gray-300 px-4 focus:border-black focus:ring-0 sm:text-sm"
            />
            <button
              type="submit"
              disabled={disabled}
              className={`${
                disabled
                  ? 'cursor-not-allowed border-gray-300 bg-gray-100 text-gray-500'
                  : 'border-black bg-black text-white hover:bg-white hover:text-black'
              } w-28 rounded-md border border-solid py-2 text-sm transition-all duration-150 ease-in-out focus:outline-none`}
            >
              {adding ? <Loader /> : 'Add'}
            </button>
          </form>

          {error && (
            <div className="mt-5 flex w-full max-w-2xl items-center space-x-2 text-left text-sm text-red-500">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                shapeRendering="geometricPrecision"
                style={{ color: '#f44336' }}
              >
                <circle cx="12" cy="12" r="10" fill="white" />
                <path d="M12 8v4" stroke="#f44336" />
                <path d="M12 16h.01" stroke="#f44336" />
              </svg>
              <p>
                Cannot add <b>{error.domain}</b> since it&apos;s already
                assigned to another project.
              </p>
            </div>
          )}
        </>
      )}

      <div className="w-full max-w-2xl">
        {domainsList
          ? domainsList.map((domain) => {
              return (
                <DomainCard
                  key={domain.id}
                  domain={domain.data()}
                  domainId={domain.id}
                />
              );
            })
          : [1, 2, 3, 4, 5].map((v) => {
              return <DomainCardPlaceholder key={v + '-index'} />;
            })}
      </div>
    </div>
  );
}
